import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataObject } from '@shared/models/data-object.model';
import { Column, deepMerge, FormControlOptions, isDetailSection, Section } from '@miq/catalog';
import { FormContainerControlBuilder } from './form-container-builder.service';
import { InputControlBuilder } from './input-control-builder.service';
import { digger } from '@cbre/digger';

interface SectionFco {
  formControlOptions: FormControlOptions<Section>;
}

interface ColumnFco {
  formControlOptions: FormControlOptions<Column>;
}

@Injectable({
  providedIn: 'root',
})
export class FormMakerService {
  constructor(
    private fb: UntypedFormBuilder,
    private sectionFormMaker: FormContainerControlBuilder,
    private columnFormMaker: InputControlBuilder
  ) {}

  makeForm(sections: Section[], data: DataObject = {}): UntypedFormGroup {
    const columns = sections.reduce((acc, s) => [...acc, ...s.columns], []);
    const rootAttrGroup = this.makeColumnsFormGroup(columns, data);
    const mainForm = this.fb.group(rootAttrGroup);
    this.createSectionsForm(sections, mainForm, data);
    return mainForm;
  }

  public makeColumnsFormGroup(columns: Column[], data: DataObject): DataObject {
    const rootAttrColumns = columns.filter(col => col.formControlOptions && !col.formControlOptions.parentControl);
    return Object.assign(
      {},
      ...rootAttrColumns.map(col => {
        const columnGroup = this._getColumnGroup(col, data);
        const unitGroup = col.unit != null ? this._getColumnGroup(col.unit, data) : {};
        return { ...columnGroup, ...unitGroup };
      })
    );
  }

  private _getColumnGroup(col: Column, data: DataObject): DataObject {
    const { formControlOptions } = col;
    const { name } = formControlOptions;
    return { [name]: [digger(data, col.getReadPath())] };
  }

  public createSectionsForm(sections: Section[], form: UntypedFormGroup, data: DataObject = {}): AbstractControl {
    const allMergedControlOptions = sections
      .filter(s => s.targetedControlOptions != null && isDetailSection(s))
      .reduce((prev, next) => deepMerge(prev, next.targetedControlOptions), {});

    Object.values(allMergedControlOptions).forEach((sectionFCO: FormControlOptions<Section>) =>
      this.createOneSection({ formControlOptions: sectionFCO }, form, data)
    );
    return form;
  }

  public createOneSection(section: SectionFco, form: UntypedFormGroup, data: DataObject = {}): AbstractControl {
    return this.sectionFormMaker.makeControlEntry(section, form, data) || form;
  }

  public createColumnsControls(
    columns: Pick<Column, 'formControlOptions'>[],
    form: UntypedFormGroup,
    data: DataObject = {}
  ): AbstractControl[] {
    return columns
      .filter(col => col.formControlOptions != null)
      .map((col: ColumnFco) => this.createOnceColumnControl(col, form, data));
  }

  public createOnceColumnControl(column: ColumnFco, form: UntypedFormGroup, data: DataObject = {}): AbstractControl {
    return this.columnFormMaker.makeControlEntry(column as Column, form, data);
  }
}
